import React, { useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Interview, InterviewStageName, InterviewNote } from '../../types/interview';
import { FiX, FiCheck, FiCalendar } from 'react-icons/fi';
import { interviewApi } from '../../services/api/interview';
import { useAuth } from '../../contexts/AuthContext';

interface InterviewNotesSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  interview: Interview | null;
  currentStage: InterviewStageName;
  nextStage?: InterviewStageName;
  onStageComplete: (stageId: number) => Promise<void>;
}

const InterviewNotesSidebar: React.FC<InterviewNotesSidebarProps> = ({
  isOpen,
  onClose,
  interview,
  currentStage,
  nextStage,
  onStageComplete
}) => {
  const [notes, setNotes] = useState<InterviewNote[]>([]);
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState('');
  const [rating, setRating] = useState(3);
  const { user } = useAuth();
  
  useEffect(() => {
    setNotes([]);
    setContent('');
    setRating(3);
  }, [interview?.id]);
  
  useEffect(() => {
    if (isOpen && interview?.id && interview?.job_id && user?.id) {
      loadNotes();
    }
    return () => {
      setNotes([]);
      setContent('');
      setRating(3);
    };
  }, [isOpen, interview?.id, interview?.job_id, user?.id]);

  const loadNotes = async () => {
    if (!interview?.id || !interview?.job_id || !user?.id) return;
    
    try {
      setLoading(true);
      const fetchedNotes = await interviewApi.getNotes(
        interview.application_id,
        interview.id,
        interview.job_id,
        user.id
      );
      setNotes(fetchedNotes || []);
    } catch (error) {
      console.error('Failed to load notes:', error);
      setNotes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!interview?.id || !content.trim() || !user?.id) return;

    try {
      await interviewApi.addNote({
        application_id: interview.application_id,
        interview_id: interview.id,
        job_id: interview.job_id,
        user_id: user.id,
        content: content.trim(),
        feedback: { overall: rating }
      });
      setContent('');
      setRating(3);
      await loadNotes();
    } catch (error) {
      console.error('Failed to add note:', error);
    }
  };

  if (!interview) {
    // Return nothing if no interview provided (hooks are already called above)
    return null;
  }

  return (
    <Transition.Root show={isOpen} as={React.Fragment}>
      <Dialog 
        as="div"
        className="fixed inset-0 z-[9999]" 
        onClose={onClose}
      >
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <Transition.Child
              as={React.Fragment}
              enter="transform transition ease-in-out duration-500"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-500"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <Dialog.Panel className="fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-xl">
                <div className="flex h-full flex-col bg-white shadow-xl">
                  {/* Header */}
                  <div className="border-b border-gray-200 px-4 py-6 sm:px-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <Dialog.Title className="text-lg font-semibold text-gray-900">
                          Interview Notes
                        </Dialog.Title>
                        <p className="mt-1 text-sm text-gray-600">
                          {currentStage} Stage
                        </p>
                      </div>
                      <button
                        onClick={onClose}
                        className="rounded-md text-gray-400 hover:text-gray-500"
                      >
                        <FiX className="h-6 w-6" />
                      </button>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 overflow-y-auto px-4 py-6 sm:px-6">
                    {/* Quick Note Input */}
                    <div className="mb-6 space-y-4">
                      <textarea
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        placeholder="Add interview notes here..."
                        className="w-full min-h-[100px] rounded-lg border border-gray-200 p-3 text-sm"
                      />
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-600">Rating:</span>
                          {[1, 2, 3, 4, 5].map((value) => (
                            <button
                              key={value}
                              onClick={() => setRating(value)}
                              className={`w-8 h-8 rounded-full ${
                                value <= rating
                                  ? 'bg-purple-100 text-purple-600'
                                  : 'bg-gray-100 text-gray-400'
                              } text-sm font-medium`}
                            >
                              {value}
                            </button>
                          ))}
                        </div>
                        <button
                          onClick={handleSubmit}
                          className="rounded-md bg-purple-600 px-3 py-2 text-sm font-semibold text-white hover:bg-purple-500"
                        >
                          Add Note
                        </button>
                      </div>
                    </div>

                    {/* Previous Notes */}
                    <div className="space-y-4">
                      {loading ? (
                        <div className="text-center py-4">
                          <span className="text-gray-500">Loading notes...</span>
                        </div>
                      ) : notes.length > 0 ? (
                        notes.map((note) => (
                          <div
                            key={note.id}
                            className="rounded-lg bg-gray-50 p-4"
                          >
                            <div className="flex items-center text-sm text-gray-500 mb-2">
                              <FiCalendar className="mr-1.5 h-4 w-4" />
                              {new Date(note.created_at).toLocaleString()}
                              {note.feedback?.overall && (
                                <span className="ml-3 inline-flex items-center rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-medium text-purple-700">
                                  Rating: {note.feedback.overall}/5
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-700">
                              {note.content}
                            </p>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-4">
                          <span className="text-gray-500">No notes yet</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Footer */}
                  {nextStage && (
                    <div className="border-t border-gray-200 px-4 py-4 sm:px-6">
                      <div className="flex justify-end gap-3">
                        <button
                          onClick={onClose}
                          className="px-3 py-2 text-sm font-semibold text-gray-900 hover:bg-gray-50 rounded-md"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => onStageComplete(interview.id)}
                          className="inline-flex items-center rounded-md bg-purple-600 px-3 py-2 text-sm font-semibold text-white hover:bg-purple-500"
                        >
                          <FiCheck className="mr-1.5 h-4 w-4" />
                          Move to {nextStage}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default InterviewNotesSidebar; 